import { useMutation } from '@apollo/client';
import { isValidSensorTagName } from '@blackbird/common/data-validator';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import * as Schema from 'generated/graphql/schema';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import DialogHeader from '@/components/dialogs/dialog-header';
import LoadingOrError from '@/components/loading/loading-or-error';
import { createPeripheral } from '@/graphql/mutations';
import { administrationDetailedGroups } from '@/graphql/queries';
import { guardedCallback } from '@/helpers/helper-functions';
import { SearchValue, isSearchValueType } from '@/helpers/input';

const useStyles = makeStyles()((theme) => ({
  createSensor: {
    marginTop: '10px',
    left: '70%',
    width: '150px',
  },
  moveToRight: {
    color: '#269AE0',
    borderColor: '#269AE0',
    position: 'absolute',
    right: '80px',
    top: 'auto',
  },
  accordion: {
    marginBottom: '2px',
    boxShadow: '1px 2px 4px 1px #888888',
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  topPadding: {
    paddingTop: theme.spacing(3),
  },
}));

type RequiredPeripheralFields = Pick<NonNullable<NonNullable<Schema.Device['peripherals']>[0]>, 'index'>;

export type RequiredDeviceFields = Pick<Schema.Device, 'status' | 'type'> & {
  peripherals: Array<RequiredPeripheralFields | null | undefined>;
};

interface Properties {
  groups: Schema.AdministrationDetailedGroupsQuery['company']['groups'];
  softwareId: string | undefined;
  device?: RequiredDeviceFields;
  companyId: string | undefined;
  open: boolean;
  onClose?: () => unknown;
  onDone: () => void;
}

const CreatePeripheralContent: FC<Properties> = ({ softwareId, onClose, onDone, device, groups }) => {
  const [t] = useTranslation();
  const { classes } = useStyles();

  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const [tagName, setTagName] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [searchValue, setSearchValue] = useState<SearchValue[]>([]);
  const [createdPeripherals, setCreatedPeripherals] = useState<
    NonNullable<Schema.CreatePeripheralMutation['createPeripheral']>[]
  >([]);

  // FIXME: Backend relies on client supplying the super and readonly groups
  const superGroupId = useMemo(() => groups.find((group) => group.name.toLowerCase() === 'super')?.id, [groups]);
  const readonlyGroupId = useMemo(() => groups.find((group) => group.name.toLowerCase() === 'read-only')?.id, [groups]);

  const loading = !groups || !softwareId || !device?.type || !device?.peripherals;

  const addedGroups = useMemo(() => {
    let newGroupIds: string[] = [];
    searchValue.forEach((sv) => {
      if (isSearchValueType(sv)) {
        newGroupIds.push(sv.id);
      }
    });
    return newGroupIds;
  }, [searchValue]);

  const [createPeripheralMutation, { loading: loadingCreatePeripheral }] = useMutation<
    Schema.CreatePeripheralMutation,
    Schema.CreatePeripheralMutationVariables
  >(createPeripheral, {
    onCompleted: async (data) => {
      const createdPeripheral = data.createPeripheral;
      if (createdPeripheral) {
        setCreatedPeripherals([...createdPeripherals, createdPeripheral]);
      }
      setTagName('');
      setName('');
      setDescription('');
      setSearchValue([]);
    },
  });

  const onIndexChange = useCallback(guardedCallback(setTagName), []);
  const onNameChange = useCallback(guardedCallback(setName), []);
  const onDescriptionChange = useCallback(guardedCallback(setDescription), []);

  const onCreatePeripheral = async () => {
    if (!softwareId) {
      console.error('Missing software id!');
      return;
    }

    if (!superGroupId || !readonlyGroupId) {
      console.error('Missing super group or read-only group!');
    }

    await createPeripheralMutation({
      variables: {
        softwareId,
        input: { index: tagName, name, description },
        // FIXME: Backend requires super and read-only group to be supplied, this is a tenmporary fix and should not rely on FE to supply this
        initialGroups:
          superGroupId && readonlyGroupId ? [superGroupId, readonlyGroupId, ...addedGroups] : [...addedGroups],
      },
      // Refetches the latest peripheral information - updates the list
      refetchQueries: [{ query: administrationDetailedGroups }],
      awaitRefetchQueries: true,
    });
  };

  const onSelectedPortChange = (selectedPort: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setTagName('');
    setName('');
    setDescription('');
    setSelectedPort(isExpanded ? selectedPort : null);
  };

  // FIXME: This piece could be simplified a great deal. We should really just
  // have a predefined mapping with proper translation keys.
  //
  // The code below is essentially just a filter and we can terminate early to
  // avoid several loops by checking type !== 'plc' at the very start.
  const renderSetupSensorPorts = () => {
    if (!device?.type || !device?.peripherals) {
      return;
    }
    const portTagNames = portsFromDeviceType(device);
    const isUnknownPortLength = portTagNames.length === 0;
    if (isUnknownPortLength) {
      return renderNewPeripheralPort('0', false);
    }
    const alreadySetupPorts = portTagNames.map((tagName) => {
      const isBBDevice = device.type !== 'plc';
      return (
        createdPeripherals.some(
          (p) => getTagName(p.peripheralId, isBBDevice) === (isBBDevice ? incrementString(tagName) : tagName),
        ) || device?.peripherals.some((p) => p?.index === tagName)
      );
    });

    let peripheralsToRender: JSX.Element[] = [];
    for (const [index, name] of portTagNames.entries()) {
      if (!alreadySetupPorts[index]) {
        // Only possible for Blackbird managed devices
        peripheralsToRender.push(renderNewPeripheralPort(name, true));
      }
    }

    return peripheralsToRender.length === 0 && createdPeripherals.length === 0 ? (
      <Typography>
        {t(['shared:noAvailablePorts'], { defaultValue: 'No available ports - all ports already set up' })}
      </Typography>
    ) : (
      peripheralsToRender
    );
  };

  // IndexIsTagName is true only for fbduo for now
  const renderNewPeripheralPort = (portIndex: string, indexIsTagName: boolean) => {
    const nameKey = `name-${portIndex}`;
    const indexKey = `index-${portIndex}`;
    const descriptionKey = `description-${portIndex}`;

    if (indexIsTagName && tagName !== portIndex && selectedPort === portIndex) {
      setTagName(portIndex);
    }
    const tagNameError = (!indexIsTagName && !tagName.length) || !isValidSensorTagName(tagName);
    const sensorNameError = !name.length;
    return (
      <Accordion
        onChange={onSelectedPortChange(portIndex)}
        expanded={selectedPort === portIndex}
        key={portIndex}
        className={classes.accordion}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography>
            {indexIsTagName
              ? `Port - ${incrementString(portIndex)}`
              : t(['shared:addSensor'], { defaultValue: 'Add new sensor' })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={`${classes.accordionDetails}`}>
          {indexIsTagName ? null : (
            <TextField
              value={indexIsTagName ? portIndex : tagName}
              id={indexKey}
              label={t(['shared:tagName'], { defaultValue: 'Tag name' })}
              variant="outlined"
              fullWidth
              disabled={indexIsTagName}
              error={tagNameError}
              helperText={
                tagNameError
                  ? t(['shared:tagNameHelper'], {
                      defaultValue: 'Please enter value using valid characters such as: a-z, 0-9, _ or .',
                    })
                  : ' '
              }
              required
              onChange={onIndexChange}
            />
          )}
          <TextField
            id={nameKey}
            label={t(['shared:name'], { defaultValue: 'Name' })}
            variant="outlined"
            value={name}
            onChange={onNameChange}
            fullWidth
            required
            error={sensorNameError}
            helperText={
              sensorNameError ? t(['shared:fieldIsRequired'], { defaultValue: 'This field is required' }) : ' '
            }
          />
          <TextField
            id={descriptionKey}
            label={t(['shared:description'], { defaultValue: 'Description' })}
            variant="outlined"
            value={description}
            helperText=" "
            fullWidth
            onChange={onDescriptionChange}
            required={false}
          />
          <Autocomplete
            isOptionEqualToValue={(option, value) =>
              isSearchValueType(option) && isSearchValueType(value) ? option.id === value.id : option === value
            }
            value={searchValue}
            multiple
            disableCloseOnSelect
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={isSearchValueType(option) ? option.label : option}
                  {...getTagProps({ index })}
                  key={`chip-${isSearchValueType(option) ? option.label : option}`}
                  disabled={isSearchValueType(option) && isLockedGroup(option.label)}
                />
              ))
            }
            getOptionDisabled={(option) => isSearchValueType(option) && isLockedGroup(option.label)}
            renderOption={(props, option, state) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={state.selected || (isSearchValueType(option) && isLockedGroup(option.label))}
                />
                {isSearchValueType(option) ? option.label : option}
              </li>
            )}
            onChange={(event, value) => setSearchValue(value)}
            id="combo-box-demo"
            getOptionLabel={(option) => (isSearchValueType(option) ? option.label : option)}
            options={generateLabels(groups)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                focused
                label={t('administration:permittedGroups', { defaultValue: 'Permitted Groups' })}
                placeholder={
                  addedGroups.length > 0
                    ? ''
                    : t(['administration:additionalGroupAccessForEntry'], {
                        defaultValue: 'Select additional groups that may access this {{entry}}',
                        entry: t('shared:sensor').toLowerCase(),
                      })
                }
              />
            )}
          />
          <Button
            className={classes.createSensor}
            onClick={onCreatePeripheral}
            disabled={!(tagName || tagName.length) || !name.length || loadingCreatePeripheral}
            color="primary"
          >
            {loadingCreatePeripheral ? (
              <CircularProgress />
            ) : (
              t(['iam:createPeripheral'], { defaultValue: 'Create Sensor' })
            )}
          </Button>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderCreatedSensorPorts = () => {
    return createdPeripherals.map((peripheral) => renderCreatedPeripheralPort(peripheral));
  };

  const renderCreatedPeripheralPort = (peripheral: Schema.CreatePeripheralMutation['createPeripheral']) => {
    if (!peripheral) {
      return null;
    }

    const readablePeripheralId = getTagName(peripheral.peripheralId, device?.type === 'fbduo');
    const portIndex = readablePeripheralId;
    const nameKey = `name-${portIndex}`;
    const indexKey = `index-${portIndex}`;
    const descriptionKey = `description-${portIndex}`;
    return (
      <Accordion
        onChange={onSelectedPortChange(portIndex)}
        expanded={selectedPort === portIndex}
        key={portIndex}
        className={`${classes.accordion}`}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography>{`Port - ${readablePeripheralId}`}</Typography>

          <Chip className={classes.moveToRight} label="Created" variant="outlined" size="small" />
        </AccordionSummary>
        <AccordionDetails className={`${classes.accordionDetails}`} color="green">
          <TextField
            id={indexKey}
            helperText=" "
            label={t(['shared:tagName'], { defaultValue: 'Tag name' })}
            variant="outlined"
            value={readablePeripheralId}
            fullWidth
            disabled
          />
          <TextField
            id={nameKey}
            helperText=" "
            label={t(['shared:name'], { defaultValue: 'Name' })}
            variant="outlined"
            value={peripheral.name}
            fullWidth
            disabled
          />
          <TextField
            id={descriptionKey}
            helperText=" "
            label={t(['shared:description'], { defaultValue: 'Description' })}
            variant="outlined"
            value={peripheral.description || ' '}
            fullWidth
            disabled
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  return loading ? (
    <>
      <DialogHeader title={t(['shared:setupSensor'], { defaultValue: 'Set up sensor' })} onClose={onClose} />
      <LoadingOrError loading={loading} />{' '}
    </>
  ) : (
    <>
      <DialogHeader title={t(['shared:setupSensor'], { defaultValue: 'Set up sensor' })} onClose={onClose} />
      <DialogContent className={classes.topPadding}>
        {renderCreatedSensorPorts()}
        {renderSetupSensorPorts()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDone} disabled={createdPeripherals.length === 0} color="primary">
          {t(['shared:done'], { defaultValue: 'Done' })}
        </Button>
      </DialogActions>
    </>
  );
};

const isLockedGroup = (groupName: string) => {
  const groupNameLowercase = groupName.toLowerCase();
  return groupNameLowercase === 'super' || groupNameLowercase === 'read-only';
};

const getTagName = (peripheralId: string, isBBDevice: boolean) => {
  const [, ...splitPeripheralId] = peripheralId.split('-');
  const tagName = splitPeripheralId.join('-');
  return isBBDevice ? incrementString(tagName) : tagName;
};

const parseSemVer = (input: string): null | [number, number, number] => {
  let version: [number, number, number] = [0, 0, 0];
  const sliced = input.split('.');
  if (sliced.length !== 3) {
    return null;
  }
  for (const [index, slice] of input.split('.').entries()) {
    let numeric = parseInt(slice, 10);
    if (isNaN(numeric)) {
      return null;
    }
    version[index] = numeric;
  }
  return version;
};

const portsFromDeviceType = (device: Pick<Schema.Device, 'status' | 'type'>): string[] => {
  switch (device.type) {
    case 'fbduo':
      const firmwareVersionParsed = device?.status?.firmwareVersions?.application
        ? parseSemVer(device.status.firmwareVersions.application)
        : null;

      if (firmwareVersionParsed && firmwareVersionParsed[0] >= 3) {
        if (device.status.hardwareVersion === 'v2') {
          return ['p1', 'p2'];
        } else if (device.status.hardwareVersion === 'v1' || !device.status.hardwareVersion) {
          return ['p1_1', 'p2_1'];
        } else {
          console.error('Failed to get ports for duo hardware version', device.status.hardwareVersion);
          return [];
        }
      } else {
        return ['0', '1'];
      }
    case 'fb':
      return ['0'];
    case 'fbview':
    case 'fbpro':
      return [];
    default:
      console.error('ERROR! Could not map port! Device type: ', device.type);
      return [];
  }
};

const incrementString = (numberString: string) => {
  return (parseInt(numberString, 10) + 1).toString();
};

const generateLabels = (groups: Schema.AdministrationDetailedGroupsQuery['company']['groups']) => {
  return groups.map((group) => {
    return { label: group.name, id: group.id };
  });
};

export default CreatePeripheralContent;
